import { ApiError } from "./errors";
import { sessionCoordinator, sessionExpiredError } from "./sessionLifecycle";

export interface SessionCoordinator {
  getAccessToken(): Promise<string | null>;
  refreshAccessToken(): Promise<string | null>;
  expireSession(): Promise<void>;
}

interface ApiClientOptions {
  coordinator: SessionCoordinator;
  fetchImpl?: typeof fetch;
}

type ApiErrorPayload = {
  code?: string;
  message?: string;
  error?: string | { code?: string; message?: string };
};

function defaultUserMessage(status: number) {
  if (status === 400) return "请求内容有误，请检查后重试";
  if (status === 403) return "当前账号没有执行此操作的权限";
  if (status === 404) return "请求的内容不存在或已被移除";
  if (status === 409) return "数据已发生变化，请刷新后重试";
  if (status === 429) return "请求过于频繁，请稍后再试";
  if (status >= 500) return "服务器暂时无法完成请求，请稍后重试";
  return "请求暂时无法完成，请稍后重试";
}

function payloadDetails(payload: ApiErrorPayload | null) {
  if (!payload) return { code: undefined, message: undefined };
  if (typeof payload.error === "object") {
    return {
      code: payload.error.code || payload.code,
      message: payload.error.message || payload.message,
    };
  }
  return {
    code: payload.code,
    message: payload.error || payload.message,
  };
}

async function readErrorPayload(response: Response) {
  return response.clone().json().catch(() => null) as Promise<ApiErrorPayload | null>;
}

export function createApiClient(options: ApiClientOptions) {
  const fetchImpl = options.fetchImpl ?? fetch;
  let refreshPromise: Promise<string | null> | null = null;
  let expiryPromise: Promise<void> | null = null;

  function refreshOnce() {
    if (!refreshPromise) {
      refreshPromise = options.coordinator.refreshAccessToken().finally(() => {
        refreshPromise = null;
      });
    }
    return refreshPromise;
  }

  function expireOnce() {
    if (!expiryPromise) expiryPromise = options.coordinator.expireSession();
    return expiryPromise;
  }

  async function send(path: string, init: RequestInit, token: string | null) {
    const headers = new Headers(init.headers);
    if (init.body && !(init.body instanceof FormData) && !headers.has("Content-Type")) {
      headers.set("Content-Type", "application/json");
    }
    if (token) headers.set("Authorization", "Bearer " + token);
    return fetchImpl(path, { ...init, headers });
  }

  return async function apiFetch(path: string, init: RequestInit = {}) {
    let token = await options.coordinator.getAccessToken();
    let response = await send(path, init, token);

    if (response.status === 401) {
      token = await refreshOnce();
      if (token) response = await send(path, init, token);
      if (!token || response.status === 401) {
        const payload = await readErrorPayload(response);
        const details = payloadDetails(payload);
        await expireOnce();
        throw sessionExpiredError(details.code || details.message || "Worker returned 401");
      }
    }

    if (!response.ok) {
      const payload = await readErrorPayload(response);
      const details = payloadDetails(payload);
      throw new ApiError({
        code: details.code || "API_ERROR",
        userMessage: defaultUserMessage(response.status),
        technicalMessage: details.message || "HTTP " + response.status,
        status: response.status,
        retryable: response.status === 408 || response.status === 429 || response.status >= 500,
      });
    }
    return response;
  };
}

export const apiFetch = createApiClient({ coordinator: sessionCoordinator });

export async function apiRequest<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await apiFetch(path, init);
  try {
    return await response.json() as T;
  } catch (error) {
    throw new ApiError({
      code: "INVALID_API_RESPONSE",
      userMessage: "服务器响应格式异常，请稍后重试",
      technicalMessage: error instanceof Error ? error.message : "Response JSON parse failed",
      status: response.status,
      retryable: true,
      cause: error,
    });
  }
}

export async function apiBlob(path: string, init?: RequestInit) {
  const response = await apiFetch(path, init);
  return response.blob();
}
